import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Clock, Mail, Plus, Users, ArrowRight, Building2 } from "lucide-react";

/**
 * Standalone preview of a redesigned Step 1 (consent) screen for the
 * applicant flow. Reachable only at `/demo/applicant-flow?step=1new`.
 *
 * NOT WIRED TO ANY BACKEND. Pure presentation — no API calls, no flow
 * context mutation, no navigation. The "Start Identity Review" button is
 * inert beyond toggling local consent state.
 */
export default function ApplicantFlowStep1New() {
  const [consent, setConsent] = useState(false);
  const firstName = "Patrick";
  const orgName = "Rockwell Properties";

  return (
    <div className="min-h-screen bg-[#f5f3ee]">
      {/* Lavender trust banner */}
      <div className="w-full bg-[#ece8fb] border-b border-[#d9d2f5]">
        <div className="max-w-5xl mx-auto px-6 py-2.5 flex items-center justify-center gap-2 text-[13px] text-[#5b3fd9]">
          <Shield className="h-4 w-4" />
          <span>Encrypted &amp; private · Connections disconnected after review</span>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-6 py-10">
        <Card className="rounded-2xl border border-border/60 shadow-sm">
          <CardContent className="p-8 space-y-6">
            {/* Org chip */}
            <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-foreground/80">
              <Building2 className="h-3.5 w-3.5" />
              {orgName}
            </div>

            {/* Headline */}
            <div className="space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground leading-tight">
                Hi {firstName}, let's confirm your identity
              </h1>
              <p className="text-[15px] text-muted-foreground">
                A quick process to help keep your application moving.
              </p>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#e8f5e0] px-3 py-1 text-xs font-medium text-[#437a22]">
                <Clock className="h-3.5 w-3.5" />
                Takes about 3 minutes
              </div>
            </div>

            <div className="border-t border-border/60" />

            {/* Steps */}
            <ul className="space-y-5">
              <StepRow
                icon={<Mail className="h-4 w-4 text-[#5b3fd9]" />}
                title="Connect your email account"
                desc="Sign in with Google or Microsoft to confirm it's yours"
              />
              <StepRow
                icon={<Plus className="h-4 w-4 text-[#5b3fd9]" />}
                title="Link at least one account"
                desc="Connect an account that helps show it's really you"
              />
              <StepRow
                icon={<Users className="h-4 w-4 text-[#5b3fd9]" />}
                title="Ask someone who knows you"
                desc="They'll get a short message to confirm you're legit"
              />
            </ul>

            {/* Consent box */}
            <label
              htmlFor="consent-new"
              className="flex gap-3 items-start rounded-lg border border-border bg-muted/30 p-4 cursor-pointer"
            >
              <Checkbox
                id="consent-new"
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                className="mt-0.5"
              />
              <span className="text-[13px] leading-relaxed text-foreground/80">
                I agree to share the identity details I choose to provide with{" "}
                <span className="font-semibold text-foreground">{orgName}</span>{" "}
                for this application. DigaVouch does not make the rental decision.
              </span>
            </label>

            {/* CTA */}
            <Button
              disabled={!consent}
              className="w-full h-12 rounded-xl text-[15px] font-medium text-white"
              style={{
                backgroundColor: consent ? "#5b3fd9" : "#c5bfed",
              }}
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Start Identity Review
            </Button>

            {/* Decline */}
            <div className="text-center">
              <button
                type="button"
                className="text-[13px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              >
                Decline &amp; return to application
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StepRow({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#ece8fb]">
        {icon}
      </div>
      <div className="space-y-0.5">
        <div className="text-[15px] font-semibold text-foreground">{title}</div>
        <div className="text-[13px] text-muted-foreground">{desc}</div>
      </div>
    </li>
  );
}
